import { useTranslations } from '@power-core/toolkit';
import { useCallback, useState } from 'react';

import { sendHelpMailApi } from '@/modules/app-config/api/app-config.api';
import type { IEmailHelp } from '@/modules/app-config/types/app-config.type';
import { useAppContext } from '@/modules/app-context/use-app-context';
import { arrayBufferToBase64 } from '@/modules/utils/array-buffer-to-base64';
import { useNavigation } from '@/modules/utils/custom-hooks/use-navigation';
import { validateEmail } from '@/modules/utils/validate-email';
import { useAuth } from '@power-core/toolkit';

const ACCEPTED_FILE_EXTENSIONS = [
	'dxf',
	'png',
	'jpeg',
	'avi',
	'json',
	'bmp',
	'csv',
	'doc',
	'docx',
	'jpg',
	'm4a',
	'avi',
	'txt',
	'pptx',
	'xlsx',
	'mp4',
	'mpeg',
	'pdf',
	'ppsx',
	'psd',
	'rtf',
	'tif',
	'tiff',
	'wmv',
	'gif',
	'xml',
	'docweb',
	'curves',
	'xls',
	'xlsm',
];

type SupportData = {
	email: string;
	files: File[];
	subject: string;
	description: string;
};

export const useSupportParams = () => {
	const t = useTranslations();
	const { showToast, appConfig } = useAppContext();
	const { auth } = useAuth();
	const { removeAllSearchParams } = useNavigation();
	const [data, setData] = useState<SupportData>({
		email: '',
		files: [],
		subject: '',
		description: '',
	});
	const [error, setError] = useState<string>('');

	const validateFile = (file: File) => {
		if (!file) return false;

		const splitName = file.name.split('.');
		const type = splitName[splitName.length - 1];
		const isValid = ACCEPTED_FILE_EXTENSIONS.includes(type);

		if (!isValid) {
			showToast({ message: t('T_FILE_NOT_VALID'), variant: 'error' });
		}
		return isValid;
	};

	const handleChange = (newData: Partial<SupportData>) => {
		setError('');
		setData((prev) => ({ ...prev, ...newData }));
	};

	const validateAndSetFile = (file: File) => {
		if (!file) return;

		const isFileValid = validateFile(file);

		if (file && isFileValid) {
			const files = data.files?.length ? [...data.files, file] : [file];

			handleChange({ files });
		}
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		validateAndSetFile(e.dataTransfer.files[0]);
	};

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files) return;

		validateAndSetFile(e.target.files[0]);
	};

	const sendHelpMail = (helpInformation: IEmailHelp) => {
		if (data.email && !validateEmail(data.email)) {
			setError(t('T_INVALID_EMAIL'));
			return;
		}

		sendHelpMailApi(helpInformation).then((data) => {
			data.statusCode === 202
				? showToast({ message: t('T_MAIL_SENT'), variant: 'success' })
				: showToast({ message: t('T_MAIL_ERROR'), variant: 'error' });
		});
		removeAllSearchParams();
	};

	const handleSubmit = useCallback(async () => {
		if (!data.subject || !data.description) return;

		const helpInformation: IEmailHelp = {
			From: data.email || auth?.data?.user?.email || '',
			Project: null,
			AttachedFiles: [],
			ProjectName: appConfig.appName,
			Subject: `${appConfig.appName}: ${data.subject}`,
			MailBody: `${data.description}\nFrom: ${window.location.hostname}`,
		};

		try {
			if (data.files.length > 0) {
				const attachments = await Promise.all(
					data.files.map(async (file) => {
						const buffer = await file.arrayBuffer();
						return {
							content: arrayBufferToBase64(buffer),
							type: file.type || file.name.split('.').pop() || '',
							name: file.name,
						};
					}),
				);
				helpInformation.AttachedFiles.push(...attachments);
			}
			sendHelpMail(helpInformation);
		} catch (error) {
			console.error(error);
		}
	}, [data, auth?.data, appConfig, sendHelpMail]);

	const convertBytesToMB = (bytes: number) => (bytes / 1024 ** 2).toFixed(2);

	return {
		t,
		data,
		error,
		handleChange,
		handleDrop,
		handleFileSelect,
		handleSubmit,
		convertBytesToMB,
	};
};
